import React, { useEffect, useMemo, useRef, useState } from "react";
import { Card } from "antd";
import { FileTextOutlined, GlobalOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import "./SocialListingAgent.scss";
import {
  fetchSocialListeningHistory,
  SocialListeningHistoryItem,
} from "src/services/SocialListeningAPIs";
import { useTranslation } from "react-i18next";
import { useLanguage } from "src/hooks/useLanguage";
import { formatNumber, getNumberLocale } from "src/utils/numberFormat";
import { getDemoSocialListeningHistory } from "./demoSocialListeningReports";

const CompetitionOverview: React.FC = () => {
  const navigate = useNavigate();
  const analysisSectionRef = useRef<HTMLDivElement | null>(null);
  const [historyItems, setHistoryItems] =
    useState<SocialListeningHistoryItem[]>([]);
  const { t, i18n } = useTranslation();
  useLanguage();
  const numberLocale = useMemo(
    () => getNumberLocale(i18n.language),
    [i18n.language]
  );

  const localizeNumbersInText = (value: string) =>
    value.replace(/\d[\d,]*/g, match => {
      const normalizedValue = Number(match.replace(/,/g, ""));
      if (Number.isNaN(normalizedValue)) {return match;}
      return formatNumber(normalizedValue, {}, i18n.language);
    });

  const recentHistory = useMemo(() => {
    const demoHistory = getDemoSocialListeningHistory(i18n.language);
    const merged = [...demoHistory, ...historyItems].sort((a, b) => {
      const aTime = new Date(a.updated_at || a.created_at || 0).getTime();
      const bTime = new Date(b.updated_at || b.created_at || 0).getTime();
      return bTime - aTime;
    });
    return merged.slice(0, 2);
  }, [historyItems, i18n.language]);

  const handleScrollToAnalysis = () => {
    analysisSectionRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    const userIdRaw = localStorage.getItem("user_id");
    const userId = Number(userIdRaw);
    if (userIdRaw === null || Number.isNaN(userId)) {return;}

    fetchSocialListeningHistory(userId)
      .then(items => setHistoryItems(items || []))
      .catch(error => {
        console.error("Social listening history error:", error);
      });
  }, []);

  return (
    <div className="competition-container">
      {/* Header Section */}
       <div className="social-hero-graphic">
          <img
            src="/Icons/social-listening.svg"
            alt={t("socialListingAgent.heroAlt")}
          />
        </div>
      <div className="competition-header">
       
        <h1 className="competition-title">
          {t("socialListingAgent.heroTitleLine1")}
          <br />
          {t("socialListingAgent.heroTitleLine2")}
        </h1>
        <p className="competition-subtitle">
          {t("socialListingAgent.heroSubtitleLine1")}
          <br />
          {t("socialListingAgent.heroSubtitleLine2")}
        </p>
        <button
          className="button button--primary hero-cta"
          type="button"
          onClick={handleScrollToAnalysis}
        >
          {t("socialListingAgent.tryNow")}
        </button>
      </div>

      {/* Card Section */}
      <div className="competition-card-grid">
        <Card className="competition-card" bordered={false}>
          <div className="card-icon-circle">
            <img src="/Icons/Deployment-unit.svg" className="card-icon"></img>
          </div>
          <h3 className="social-card-title">
            {t("socialListingAgent.cardAiTitle")}
          </h3>
          <p className="card-description">
            {t("socialListingAgent.cardAiDescription")}
          </p>
        </Card>

        <Card className="competition-card" bordered={false}>
          <div className="card-icon-circle">
            <GlobalOutlined />
          </div>
          <h3 className="social-card-title">
            {t("socialListingAgent.cardRegionTitle")}
          </h3>
          <p className="card-description">
            {t("socialListingAgent.cardRegionDescription")}
          </p>
        </Card>

        <Card className="competition-card" bordered={false}>
          <div className="card-icon-circle">
            <img src="/Icons/Statistics.svg" className="card-icon"></img>
          </div>
          <h3 className="social-card-title">
            {t("socialListingAgent.cardActionableTitle")}
          </h3>
          <p className="card-description">
            {t("socialListingAgent.cardActionableDescription")}
          </p>
        </Card>
      </div>

      {/* New "Before I begin" Section */}
      <div className="data-scope-section">
        <Card className="data-scope-card" bordered={false}>
          <h2>{t("socialListingAgent.scopeTitle")}</h2>
          <ul className="scope-grid">
            <li>
              <span className="scope-label">
                {t("socialListingAgent.scopeDataSourceLabel")}
              </span>
              <span className="scope-text">
                {t("socialListingAgent.scopeDataSourcePrefix")}{" "}
                <span className="scope-emphasis">
                  {localizeNumbersInText(
                    t("socialListingAgent.scopeDataSourceEmphasis")
                  )}
                </span>
                .
              </span>
            </li>
            <li>
              <span className="scope-label">
                {t("socialListingAgent.scopeVerificationLabel")}
              </span>
              <span className="scope-text">
                {t("socialListingAgent.scopeVerificationPrefix")}{" "}
                <span className="scope-emphasis">
                  {t("socialListingAgent.scopeVerificationEmphasis")}
                </span>
                , {t("socialListingAgent.scopeVerificationMiddle")}{" "}
                <span className="scope-emphasis">
                  {localizeNumbersInText(
                    t("socialListingAgent.scopeVerificationTopFive")
                  )}
                </span>
                . {t("socialListingAgent.scopeVerificationSuffix")}
              </span>
            </li>
            <li>
              <span className="scope-label">
                {t("socialListingAgent.scopeCommentsLabel")}
              </span>
              <span className="scope-text">
                {t("socialListingAgent.scopeCommentsPrefix")}{" "}
                <span className="scope-emphasis">
                  {localizeNumbersInText(
                    t("socialListingAgent.scopeCommentsEmphasis")
                  )}
                </span>{" "}
                {t("socialListingAgent.scopeCommentsSuffix")}
              </span>
            </li>
            <li>
              <span className="scope-label">
                {t("socialListingAgent.scopeMonthlyLimitLabel")}
              </span>
              <span className="scope-text">
                {t("socialListingAgent.scopeMonthlyLimitPrefix")}{" "}
                <span className="scope-emphasis">
                  {localizeNumbersInText(
                    t("socialListingAgent.scopeMonthlyLimitEmphasis")
                  )}
                </span>
                .
              </span>
            </li>
            <li>
              <span className="scope-label">
                {t("socialListingAgent.scopeSessionLimitLabel")}
              </span>
              <span className="scope-text">
                {t("socialListingAgent.scopeSessionLimitPrefix")}{" "}
                <span className="scope-emphasis">
                  {localizeNumbersInText(
                    t("socialListingAgent.scopeSessionLimitEmphasis")
                  )}
                </span>{" "}
                {t("socialListingAgent.scopeSessionLimitSuffix")}
              </span>
            </li>
          </ul>
          <p className="scope-note">
            {t("socialListingAgent.scopeNote")}
          </p>
        </Card>

        {/* Buttons Section */}
        <div className="analysis-buttons" ref={analysisSectionRef}>
          <Card className="analysis-card" bordered={false}>
            <img src="/Icons/graph.svg" className="analysis-icon purple" />
            <h3>{t("socialListingAgent.analyzeBrandTitle")}</h3>
            <p>
              {t("socialListingAgent.analyzeBrandLine1")}
              <br />
              {t("socialListingAgent.analyzeBrandLine2")}
            </p>
<button
  className="button button--primary"
  onClick={() => navigate("brand-analysis")}
>
  {t("socialListingAgent.startBrandAnalysis")}
</button>
          </Card>

          <Card className="analysis-card" bordered={false}>
             <img src="/Icons/Globe-card.svg" className="analysis-icon purple" />
            <h3>{t("socialListingAgent.competitiveAnalysisTitle")}</h3>
            <p>
              {t("socialListingAgent.competitiveAnalysisLine1")}
              <br />
              {t("socialListingAgent.competitiveAnalysisLine2")}
            </p>
            <button
  className="button button--primary"
  onClick={() => navigate("competitive-analysis")}
>
  {t("socialListingAgent.startCompetitiveAnalysis")}
</button>
          </Card>
        </div>
      </div>

      <div className="recent-reports">
        <Card className="recent-reports-card" bordered={false}>
          <div className="recent-reports-header">
            <h3 className="recent-reports-title">
              {t("socialListingAgent.recentReports")}
            </h3>
            <button
              type="button"
              className="button button--primary view-all-button"
              onClick={() => navigate("/account/social-listing-agent/history")}
            >
              {t("socialListingAgent.viewAll")}
            </button>
          </div>

          <div className="recent-reports-list">
            {recentHistory.length === 0 ? (
              <div className="recent-report-empty">
                {t("socialListingAgent.noReports")}
              </div>
            ) : (
              recentHistory.map((item, index) => {
                const title =
                  item.report_title ||
                  item.company_name ||
                  `${t("socialListingAgent.report")} ${formatNumber(
                    index + 1,
                    {},
                    i18n.language
                  )}`;
                const dateValue = item.updated_at || item.created_at;
                const formattedDate = dateValue
                  ? new Date(dateValue).toLocaleDateString(
                      numberLocale,
                      {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      }
                    )
                  : "";

                const analysisId = item.analysis_id;
                return (
                  <button
                    key={item.analysis_id ?? item.id ?? `${title}-${index}`}
                    type="button"
                    className="recent-report-item"
                    onClick={() => {
                      if (!analysisId) {return;}
                      navigate(`/account/social-listing-agent/${analysisId}`);
                    }}
                  >
                    <div className="recent-report-icon">
                      <FileTextOutlined />
                    </div>
                    <div className="recent-report-info">
                      <div className="recent-report-title">{title}</div>
                      {formattedDate && (
                        <div className="recent-report-date">{formattedDate}</div>
                      )}
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </Card>
      </div>
    </div>
  );
};

export default CompetitionOverview;


